import { APP_ENV } from "@shared/env";
import { getShimmerBlurDataURL } from "@shared/utils/styles/shimmer";
import NextImage, { ImageProps } from "next/image";
import { CDNImageSharedProps } from "./CDNImage.props";
import { useCDNImage } from "./useCDNImage";
import { useImageLoader } from "./useImageLoader.web";

export type CDNImageProps = ImageProps & CDNImageSharedProps;

/**
 * To know when to use blur as the placeholder:
 * Use `blur` For:
 * - Large, high-resolution images
 * - Hero banners and key visuals
 * - Above-the-fold content
 * - Complex, detailed images
 * - Slow networks or visually rich websites
 *
 * Avoid `blur` For:
 * - Small, lightweight images
 * - Icons or decorative elements
 * - Below-the-fold lazy-loaded images
 * - Simple graphics or logos
 * - Performance-critical applications
 * Worth noting:
 * - When Placeholder = blur, getShimmerBlurDataURL is calculated and then the placeholder displays.
 * - We want to avoid blur for scenarios above because the calculation might take longer than the load time
 * - And we get a split instant of a grey background fighting with the already loaded image.
 */

const CDNImage = ({
  aspectRatio = undefined,
  useServerlessResize = true,
  missingFromCDN = false,
  style = {},
  responsive = false,
  placeholder = "blur",
  decorative = false,
  ...props
}: CDNImageProps) => {
  const { parsePixelString } = useCDNImage();
  const { imageLoader, serverlessResizeImageLoader } = useImageLoader();
  const {
    width,
    height,
    src,
    alt,
    "aria-label": ariaLabel,
    "aria-hidden": ariaHidden,
  } = props;
  let enableServerlessResize = useServerlessResize;

  // If file ends with .svg, use the default loader
  // because the serverless resize API doesn't support SVGs
  if (src && src.endsWith(".svg")) {
    enableServerlessResize = false;
  }

  // Default style for the image
  const defaultStyle: ImageProps["style"] = {
    objectFit: props.objectFit ?? "cover",
  };

  if (responsive) {
    defaultStyle.width = "100%";
    defaultStyle.height = "auto";
  } else if (width && height) {
    defaultStyle.width = width;
    defaultStyle.height = height;
  }

  // Combine the default style with the style passed in
  const combinedStyle = { ...defaultStyle, ...style };

  // Handle accessibility props
  const accessibilityProps = decorative
    ? { alt: "", "aria-hidden": true as const }
    : {
        alt,
        "aria-label": ariaLabel,
        "aria-hidden": ariaHidden,
      };

  // S3 CDN images don't work locally
  if (enableServerlessResize && APP_ENV !== "local") {
    return (
      <NextImage
        style={combinedStyle}
        {...props}
        {...accessibilityProps}
        loader={({ ...args }) =>
          serverlessResizeImageLoader({
            ...args,
            aspectRatio,
            missingFromCDN: Boolean(missingFromCDN),
          })
        }
        blurDataURL={getShimmerBlurDataURL(
          parsePixelString(width),
          parsePixelString(height)
        )}
        placeholder={placeholder}
      />
    );
  }
  return (
    <NextImage
      style={combinedStyle}
      {...props}
      {...accessibilityProps}
      loader={imageLoader}
      blurDataURL={getShimmerBlurDataURL(
        parsePixelString(width),
        parsePixelString(height)
      )}
      width={width}
      height={height}
      placeholder={placeholder}
    />
  );
};

export default CDNImage;
